// V4 — Command-card CSS. Split from hudStyles.ts to stay under the 600-line
// per-file cap (Rule 15). Selectors mirror the rest of the HUD context panel
// (`hud-context__*`) so the same parent rules (background, clip-path, layout
// anchoring) still apply.
//
// Idempotent on repeat calls; no runtime state.

let injected = false;

export function injectCommandCardStyles(): void {
  if (injected) return;
  if (typeof document === 'undefined') return;
  injected = true;
  const style = document.createElement('style');
  style.textContent = COMMAND_CARD_CSS;
  document.head.appendChild(style);
}

const COMMAND_CARD_CSS = `
/* ── Squad portrait header ─────────────────────────────────────────────────
 * Archetype glyph + name on one row. The portrait gives the player a
 * one-glance read of which archetype is selected; the text title remains
 * for keyboard / screen-reader users.
 */
.hud-context__header { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.hud-context__portrait {
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 700; line-height: 1;
  border: 1px solid #3a4c60;
  background: linear-gradient(180deg, rgba(36,46,60,0.95), rgba(20,28,38,0.95));
  box-shadow: inset 0 0 8px rgba(106,216,255,0.18), 0 0 6px rgba(0,0,0,0.5);
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
  flex: 0 0 auto;
}
.hud-context__portrait[data-archetype="recon"]    { color: #6ad8ff; border-color: #2a5070; }
.hud-context__portrait[data-archetype="assault"]  { color: #ff8060; border-color: #60281a; }
.hud-context__portrait[data-archetype="engineer"] { color: #ffe680; border-color: #4a4010; }
.hud-context__portrait[data-archetype="shield"]   { color: #4ad0b8; border-color: #1a5048; }
.hud-context__header .hud-context__title { margin-bottom: 0; }
.hud-context__name-stack {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}
.hud-context__tagline {
  font-size: 9px;
  letter-spacing: 0.04em;
  color: #8c95a0;
  text-transform: none;
  line-height: 1.3;
}

/* ── Clickable command grid (Q-ability, E-ability) ─────────────────────────
 * V1.7 narrowed from 4 cells to 2 — the player-facing Attack-Move and Stop
 * verbs were removed (squads auto-engage in-range enemies via CombatSystem,
 * so plain Move covers what Attack-Move used to). Icon-first cell so the
 * player can scan + click without reading tiny text. A cooldown bar drains
 * across the bottom of the cell; a hotkey badge sits top-right for keyboard
 * parity.
 */
.hud-context__commands {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-top: 10px;
}
.hud-context__cmd {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 4px 10px;
  min-height: 56px;
  background: linear-gradient(180deg, rgba(36,46,60,0.9), rgba(20,28,38,0.95));
  color: #eef3f8;
  border: 1px solid #3a4c60;
  border-radius: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: center;
  transition: border-color 120ms, color 120ms, background 120ms;
}
.hud-context__cmd:hover:not(:disabled) {
  border-color: #6ad8ff; color: #6ad8ff;
  background: linear-gradient(180deg, rgba(42,58,76,0.95), rgba(24,32,42,0.95));
}
.hud-context__cmd:active:not(:disabled) { transform: scale(0.96); }
.hud-context__cmd:disabled { opacity: 0.4; cursor: not-allowed; }
.hud-context__cmd[data-armed="true"] {
  border-color: #f5e76e; color: #f5e76e;
  background: linear-gradient(180deg, rgba(60,52,16,0.95), rgba(30,26,8,0.95));
  box-shadow: 0 0 8px rgba(245,231,110,0.45);
}
.hud-context__cmd-icon { font-size: 20px; line-height: 1; }
.hud-context__cmd-label {
  font-size: 10px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px;
}
.hud-context__cmd-key {
  position: absolute;
  top: 3px; right: 4px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #f5e76e;
  background: rgba(20, 18, 6, 0.85);
  border: 1px solid #f5e76e;
  border-radius: 3px;
  padding: 1px 4px;
  line-height: 1.2;
  min-width: 14px;
  text-align: center;
  z-index: 1;
}
.hud-context__cmd:disabled .hud-context__cmd-key { color: #6a727a; border-color: #2a3038; }
/* Cooldown overlay — width is set inline by the renderer to
 * (1 - readyRatio) * 100%, so the bar drains as the cooldown ticks down. */
.hud-context__cmd-cd {
  position: absolute;
  inset: auto 0 0 0;
  height: 3px;
  background: #4a8ed0;
  width: 0%;
  transition: width 80ms linear;
}
.hud-context__cmd[data-ready="true"] {
  border-color: #4ad06b; color: #b6f0c5;
  animation: hud-ability-ready-pulse 1.5s ease-in-out infinite;
}
.hud-context__cmd[data-ready="true"] .hud-context__cmd-key { color: #4ad06b; border-color: #4ad06b; }
.hud-context__cmd[data-kind="ability"] { border-color: #4a4020; }
.hud-context__cmd[data-kind="ability"]:not(:disabled):not([data-armed="true"]):hover {
  border-color: #c8b840; color: #f5e76e;
}

/* ── Production tiles (icon + name + cost·time) ────────────────────────────
 * Replaces the old single-line "Recon (60S, 12s)" buttons. A 4-column grid
 * of icon tiles lets the player scan archetypes by color/glyph rather than
 * by reading. Cost+time stays on a small meta line.
 */
.hud-context__buttons {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.hud-context__btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 4px;
  min-height: 64px;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: none;
}
.hud-context__btn-icon { font-size: 22px; line-height: 1; }
.hud-context__btn-name { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; }
.hud-context__btn-meta { font-size: 10px; color: #8c95a0; }
.hud-context__btn[data-archetype="recon"]    .hud-context__btn-icon { color: #6ad8ff; }
.hud-context__btn[data-archetype="assault"]  .hud-context__btn-icon { color: #ff8060; }
.hud-context__btn[data-archetype="engineer"] .hud-context__btn-icon { color: #f5e76e; }
.hud-context__btn[data-archetype="shield"]   .hud-context__btn-icon { color: #4ad0b8; }

/* ── Build row inside the Core/Hub command card ───────────────────────────
 * Build actions live INSIDE the producing structure's command card now —
 * the structure IS the builder. The stand-alone bottom-center BuildPanel
 * becomes a placement indicator while build mode is armed.
 */
.hud-context__build-section {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #2a3038;
}
.hud-context__build-title {
  font-size: 10px;
  letter-spacing: 0.14em;
  color: #8c95a0;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.hud-context__build-row { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; }
.hud-context__build-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 4px;
  min-height: 60px;
  background: linear-gradient(180deg, rgba(36,46,60,0.9), rgba(20,28,38,0.95));
  color: #eef3f8;
  border: 1px solid #3a4c60;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 120ms, color 120ms, background 120ms;
}
.hud-context__build-btn:hover:not(:disabled) {
  border-color: #c8b840; color: #f5e76e;
  background: linear-gradient(180deg, rgba(42,58,76,0.95), rgba(24,32,42,0.95));
}
.hud-context__build-btn:active:not(:disabled) { transform: scale(0.96); }
.hud-context__build-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.hud-context__build-btn[data-armed="true"] {
  border-color: #f5e76e; color: #f5e76e;
  background: linear-gradient(180deg, rgba(60,52,16,0.95), rgba(30,26,8,0.95));
  box-shadow: 0 0 8px rgba(245,231,110,0.45);
}
/* V1.7 (3.18) — effect line under cost/time. Tinted by kind so a glance
 * answers "which one does what" without reading: yellow = Hub queue, orange
 * = Turret damage. Slightly larger weight than the meta to land as the
 * primary "why" payload. */
.hud-context__btn-effect {
  font-size: 10px;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: #cfd6dd;
  margin-top: 1px;
}
.hud-context__build-btn[data-kind="production_hub"] .hud-context__btn-effect { color: #f5e76e; }
.hud-context__build-btn[data-kind="turret"]         .hud-context__btn-effect { color: #ff9670; }
/* V1.7 (3.18) — footer hint surfacing Engineer's Mini-Turret as a third
 * building type. Subdued so it reads as guidance, not a primary CTA. */
.hud-context__build-hint {
  font-size: 10px;
  color: #8c95a0;
  letter-spacing: 0.02em;
  margin-top: 6px;
  line-height: 1.3;
  text-align: center;
}
.hud-context__build-btn[data-kind="production_hub"] .hud-context__btn-icon { color: #f5e76e; }
.hud-context__build-btn[data-kind="turret"]         .hud-context__btn-icon { color: #ff9670; }
.hud-context__build-btn[data-kind="turret"] { border-color: #4a2418; }
.hud-context__build-btn[data-kind="turret"]:not(:disabled):not([data-armed="true"]):hover {
  border-color: #e2543c; color: #ff9670;
  background: linear-gradient(180deg, rgba(72,28,18,0.95), rgba(36,14,8,0.95));
}

/* BuildPanel becomes a placement indicator (visible only while armed). */
.build-panel--placement .build-panel__title { color: #f5e76e; }

/* ── Mobile responsive overrides for the new command-card pieces ──────────*/
@media (max-width: 820px), (pointer: coarse) {
  .hud-context__btn { font-size: 11px; padding: 6px 4px; min-height: 56px; }
  .hud-context__btn-icon { font-size: 18px; }
  .hud-context__btn-name { font-size: 10px; }
  .hud-context__btn-meta { font-size: 9px; }
  .hud-context__commands { gap: 4px; margin-top: 6px; }
  .hud-context__cmd { min-height: 54px; padding: 8px 2px 8px; font-size: 9px; }
  .hud-context__cmd-icon { font-size: 17px; }
  .hud-context__cmd-label { font-size: 9px; max-width: 100%; }
  .hud-context__cmd-key { font-size: 8px; top: 4px; right: 4px; padding: 1px 3px; }
  .hud-context__build-section { margin-top: 6px; padding-top: 6px; }
  .hud-context__build-title { font-size: 9px; margin-bottom: 4px; }
  .hud-context__build-btn { min-height: 50px; padding: 6px 4px; }
  .hud-context__btn-effect { font-size: 9px; }
  .hud-context__build-hint { font-size: 9px; margin-top: 4px; }
  .hud-context__header { gap: 6px; margin-bottom: 4px; }
  .hud-context__portrait { width: 32px; height: 32px; font-size: 18px; }
}
@media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
  .hud-context__cmd { min-height: 38px; padding: 4px 2px 6px; }
  .hud-context__cmd-icon { font-size: 14px; }
  .hud-context__cmd-label { font-size: 8px; }
  .hud-context__build-btn { min-height: 38px; padding: 4px 4px; }
  .hud-context__portrait { width: 26px; height: 26px; font-size: 14px; }
}
`;
